// CustomFieldValuesEditor — drawer form for create/edit of a custom-field value.
//
// ColorPicker integration strategy: hold `color` as a small piece of component
// state (useState) merged into the saved ValueInput on submit. This is cleaner
// than a Controller wrapping a visual popup widget because PrimeReact's
// ColorPicker returns an opaque hex string that doesn't need Zod validation —
// the only constraint is that it is nullable. The rest of the form (label, code,
// icon, parent, sla, active, sortOrder) is wired through ZodForm + Form* wrappers.
//
// Level derivation: the ZodForm render-prop is used to watch `parentValueId`.
// The derived level is computed from the watched value and displayed as static
// text (data-testid="derived-level"). The `level` field is NOT a form field —
// it is injected into the ValueInput on submit.
//
// Reset-on-reopen strategy: a `resetKey` counter is incremented each time `open`
// transitions to true. Passing `key={resetKey}` to `ZodForm` forces it to
// remount with fresh defaultValues, which is equivalent to calling reset() but
// works without needing to reach into react-hook-form internals. The `color`
// state is also synchronised to `initialValues?.color` inside the same effect.
//
// Save path: a `formRef` is wired through both `ZodForm` (attaches to the
// underlying <form> element) and `ResourceDrawer` (Save button calls
// formRef.current.requestSubmit()). There is one coherent save path and no
// redundant inner save button.

import { useState, useEffect, useRef, type JSX } from 'react'
import { z } from 'zod'
import { ColorPicker } from 'primereact/colorpicker'
import ResourceDrawer from '../ResourceDrawer.js'
import {
  ZodForm,
  FormText,
  FormDropdown,
  FormNumber,
  FormSwitch,
} from '../../forms/index.js'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ValueInput {
  label: string
  code: string
  color: string | null
  icon: string | null
  parentValueId: string | null
  level: number
  slaOverrideId: string | null
  isActive: boolean
  sortOrder: number
}

export interface Props {
  open: boolean
  onClose: () => void
  onSave: (input: ValueInput) => Promise<void>
  initialValues?: Partial<ValueInput>
  fieldDefinitionId: string
  availableParents: Array<{ id: string; label: string; level: number }>
  slaPolicies: Array<{ id: string; name: string }>
}

// ---------------------------------------------------------------------------
// Zod schema for the form-managed fields (color + level are outside the schema)
// ---------------------------------------------------------------------------

const ValueFormSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  code: z.string().min(1, 'Code is required'),
  icon: z.string().nullable(),
  parentValueId: z.string().nullable(),
  slaOverrideId: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int().min(0),
})

type ValueFormData = z.infer<typeof ValueFormSchema>

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CustomFieldValuesEditor({
  open,
  onClose,
  onSave,
  initialValues,
  availableParents,
  slaPolicies,
}: Props): JSX.Element {
  // Color is held in component state and merged on submit (not part of the
  // Zod schema) — ColorPicker is a visual popup widget that doesn't benefit
  // from Zod validation; nullable is the only constraint.
  const [color, setColor] = useState<string | null>(initialValues?.color ?? null)

  // resetKey forces ZodForm to remount (and therefore re-initialise useForm
  // with fresh defaultValues) each time the drawer transitions to open.
  const [resetKey, setResetKey] = useState(0)

  // formRef wires the ZodForm <form> element to the ResourceDrawer Save button.
  const formRef = useRef<HTMLFormElement>(null)

  // When the drawer transitions to open, reset both color state AND the form
  // by incrementing resetKey (which remounts ZodForm with current initialValues).
  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setColor(initialValues?.color ?? null)
      setResetKey((k) => k + 1)
    }
    prevOpenRef.current = open
  }, [open, initialValues])

  // Parent options: only nodes with level < 4 are offered (never exceed 4 levels).
  const parentOptions = [
    { label: '(none — root level)', value: '' },
    ...availableParents
      .filter((p) => p.level < 4)
      .map((p) => ({ label: `${p.label} (level ${p.level})`, value: p.id })),
  ]

  const slaOptions = [
    { label: '(none)', value: '' },
    ...slaPolicies.map((s) => ({ label: s.name, value: s.id })),
  ]

  const defaultValues: ValueFormData = {
    label: initialValues?.label ?? '',
    code: initialValues?.code ?? '',
    icon: initialValues?.icon ?? null,
    parentValueId: initialValues?.parentValueId ?? null,
    slaOverrideId: initialValues?.slaOverrideId ?? null,
    isActive: initialValues?.isActive ?? true,
    sortOrder: initialValues?.sortOrder ?? 100,
  }

  async function handleSubmit(data: ValueFormData): Promise<void> {
    // Compute level from selected parent.
    const parentId = data.parentValueId || null
    const parent = parentId ? availableParents.find((p) => p.id === parentId) : null
    const level = parent ? parent.level + 1 : 1

    const input: ValueInput = {
      label: data.label,
      code: data.code,
      color: color || null,
      icon: data.icon || null,
      parentValueId: parentId,
      level,
      slaOverrideId: data.slaOverrideId || null,
      isActive: data.isActive,
      sortOrder: data.sortOrder,
    }
    await onSave(input)
    onClose()
  }

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      title={initialValues?.label ? `Edit Value: ${initialValues.label}` : 'New Value'}
    >
      <ZodForm
        key={resetKey}
        schema={ValueFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        formRef={formRef}
        render={(methods) => {
          const watchedParentId = methods.watch('parentValueId') as string | null | undefined
          const parentId = watchedParentId || null
          const parent = parentId
            ? availableParents.find((p) => p.id === parentId)
            : null
          const derivedLevel = parent ? parent.level + 1 : 1

          return (
            <div
              data-testid="cfv-editor-form"
              aria-label="Value editor"
            >
              <FormText name="label" label="Label" required autoFocus />
              <FormText name="code" label="Code" required />

              {/* Color — held in component state, not in the ZodForm schema */}
              <div className="mb-3 flex flex-col gap-1">
                <label htmlFor="cfv-color-picker" className="text-sm font-medium">Color</label>
                <ColorPicker
                  inputId="cfv-color-picker"
                  value={color ?? undefined}
                  onChange={(e) => setColor(e.value ? String(e.value) : null)}
                />
              </div>

              <FormText name="icon" label="Icon" placeholder="e.g. pi pi-check" />

              <FormDropdown
                name="parentValueId"
                label="Parent"
                options={parentOptions}
                placeholder="(none — root level)"
              />

              {/* Derived level — read-only display */}
              <div className="mb-3 flex flex-col gap-1">
                <span className="text-sm font-medium">Level</span>
                <span
                  data-testid="derived-level"
                  className="text-sm text-gray-700"
                >
                  {derivedLevel}
                </span>
              </div>

              <FormDropdown
                name="slaOverrideId"
                label="SLA Override"
                options={slaOptions}
                placeholder="(none)"
              />

              <FormSwitch name="isActive" label="Active" />
              <FormNumber name="sortOrder" label="Sort order" min={0} />
            </div>
          )
        }}
      />
    </ResourceDrawer>
  )
}
